import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { 
  Shield, LayoutDashboard, Building2, Settings, LogOut, 
  Menu, X, ChevronDown, Crown, Globe, Database, ScrollText, Plug, Mail
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { usePageTitle } from '@/hooks/usePageTitle';

const SuperAdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasRole } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);

  // Browser tab: "CogniEtica — Canal de Denúncias · Super Admin"
  usePageTitle(null, 'Super Admin');

  // Redirect if not super admin
  useEffect(() => {
    if (user && user.role !== 'super_admin') {
      navigate('/admin');
    }
  }, [user, navigate]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/super-admin/dashboard');
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching super admin stats:', err);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'super_admin') {
      fetchStats();
    }
  }, [user, fetchStats]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/super-admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/super-admin/tenants', icon: Building2, label: 'Clientes (Tenants)' },
    { path: '/super-admin/configuracoes', icon: Settings, label: 'Configurações Plataforma' },
    { path: '/super-admin/backups', icon: Database, label: 'Backups' },
    { path: '/super-admin/auditoria', icon: ScrollText, label: 'Auditoria' },
    { path: '/super-admin/emails', icon: Mail, label: 'Registo de Emails' },
    { path: '/super-admin/provisionamento', icon: Plug, label: 'Provisionamento' },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-slate-950 text-white
        transform transition-transform duration-200 ease-in-out
        lg:translate-x-0 border-r border-slate-800
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-4 border-b border-slate-800">
            <Link to="/super-admin" className="flex items-center gap-2">
              <div className="relative">
                <Shield className="w-8 h-8 text-indigo-400" />
                <Crown className="w-4 h-4 text-amber-400 absolute -top-1 -right-1" />
              </div>
              <div>
                <span className="font-extrabold text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>CogniEtica</span>
                <span className="block text-[10px] text-indigo-400 font-bold tracking-[0.2em]">SUPER ADMIN</span>
              </div>
            </Link>
            <button 
              className="lg:hidden p-1 hover:bg-slate-800 rounded"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Quick Stats */}
          {stats && (
            <div className="p-4 border-b border-slate-800 bg-indigo-500/5">
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center">
                  <p className="text-2xl font-extrabold text-indigo-400">{stats.total_tenants}</p>
                  <p className="text-xs text-slate-400">Clientes</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-extrabold text-indigo-400">{stats.total_reports}</p>
                  <p className="text-xs text-slate-400">Denúncias</p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path, item.exact);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors font-medium
                    ${active 
                      ? 'bg-indigo-600 text-white shadow-md font-semibold' 
                      : 'text-slate-400 hover:text-slate-50 hover:bg-slate-800'}
                  `}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Link to Admin Area */}
          <div className="p-4 border-t border-slate-800">
            <Link 
              to="/admin"
              className="flex items-center gap-3 px-3 py-2 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            >
              <Globe className="w-5 h-5" />
              <span className="text-sm">Voltar ao Admin Demo</span>
            </Link>
          </div>

          {/* User Menu */}
          <div className="p-4 border-t border-slate-800">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 w-full p-2 rounded-md hover:bg-slate-800 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-300 border border-indigo-500/30">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium truncate">{user?.name}</p>
                    <p className="text-xs text-indigo-400 truncate">Super Admin</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate('/super-admin/configuracoes')}>
                  <Settings className="w-4 h-4 mr-2" />
                  Configurações
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-slate-200">
          <div className="flex items-center justify-between h-16 px-4 lg:px-8">
            <button 
              className="lg:hidden p-2 hover:bg-slate-100 rounded-md text-slate-700"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>

            <div className="flex-1 lg:hidden" />

            <div className="flex items-center gap-4">
              <Link to="/" target="_blank">
                <Button variant="outline" size="sm" className="border-indigo-200 text-indigo-700 hover:bg-indigo-50">
                  Ver Site Público
                </Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default SuperAdminLayout;
